import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { format, parseISO, startOfToday } from 'date-fns';
import { BehaviorSubject, combineLatest, Observable, Subscription } from 'rxjs';
import { distinctUntilChanged, filter, map, shareReplay, take } from 'rxjs/operators';
import { DriversService } from 'src/app/root/drivers.service';
import { SnackBarService } from 'src/app/shared/snackbar.service';
import { DriverCertification, isActiveDriver } from 'src/app/types';
import {
  GrossWeightPermit,
  GrossWeightPermitsService,
  GrossWeightStateLimit,
  permitErrorMessage,
} from './gross-weight-permits.service';

// Driver page tab for certifications and overweight permits. Certifications
// behave exactly as they did on the Profile tab; permits are new (NRG-6028).
@Component({
  selector: 'lohi-certifications-permits',
  templateUrl: './certifications-permits.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CertificationsPermitsComponent implements OnInit, OnDestroy {
  public driverId$: Observable<string>;

  public certs$: Observable<DriverCertification[]>;
  public driverCertControl = new FormControl([]);

  public stateLimits$$ = new BehaviorSubject<GrossWeightStateLimit[]>([]);
  public permits$$ = new BehaviorSubject<GrossWeightPermit[]>([]);
  public loadingPermits$$ = new BehaviorSubject<boolean>(true);
  public saving$$ = new BehaviorSubject<boolean>(false);
  // States the driver has no active permit for yet, which is what "add" offers.
  public addableStates$: Observable<GrossWeightStateLimit[]>;

  // null while the form is closed; the state being edited, or '' when adding.
  public editingState: string | null = null;
  // The state awaiting a second click to confirm removal.
  public confirmingRemoval: string | null = null;
  public readonly today = startOfToday();

  public permitForm = new FormGroup({
    stateAbbreviation: new FormControl(null, Validators.required),
    expiresOn: new FormControl(null, Validators.required),
    effectiveOn: new FormControl(null),
    permitNumber: new FormControl('', Validators.maxLength(100)),
    acknowledgedAccountability: new FormControl(false, Validators.requiredTrue),
  });

  private subscriptions = new Subscription();

  constructor(
    private driversService: DriversService,
    private permitsService: GrossWeightPermitsService,
    private snackBar: SnackBarService,
    private cd: ChangeDetectorRef,
  ) {}

  public ngOnInit(): void {
    this.driverId$ = this.driversService.currentDriver$.pipe(
      filter((driver) => !!driver && isActiveDriver(driver)),
      map((driver) => driver.id),
      distinctUntilChanged(),
      shareReplay(1),
    );
    this.certs$ = this.driversService.driverCerts$.pipe(filter((value) => !!value));

    this.addableStates$ = combineLatest([this.stateLimits$$, this.permits$$]).pipe(
      map(([limits, permits]) => {
        const held = new Set(permits.map((permit) => permit.stateAbbreviation));
        return limits.filter((limit) => !held.has(limit.stateAbbreviation));
      }),
    );

    this.setupCerts();
    this.loadStateLimits();
    this.subscriptions.add(this.driverId$.subscribe((driverId) => this.loadPermits(driverId)));
  }

  public ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  public async setupCerts() {
    const driver = await this.driversService.driverDetails$
      .pipe(
        filter((value) => !!value),
        take(1),
      )
      .toPromise();
    const certs = await this.certs$.pipe(take(1)).toPromise();
    this.driverCertControl.setValue((certs || []).filter((cert) => (driver.certs || []).includes(cert.id)));
    this.cd.markForCheck();
  }

  public certNames(certs: { name: string }[]): string[] {
    return (certs || []).map((cert) => cert.name);
  }

  public compareById(a: { id: number }, b: { id: number }): boolean {
    return a === b || (!!a && !!b && a.id === b.id);
  }

  public async updateCerts() {
    const driverId = await this.driverId$.pipe(take(1)).toPromise();
    await this.driversService.updateCerts(
      driverId,
      (this.driverCertControl.value || []).map((cert: DriverCertification) => cert.id),
    );
    this.snackBar.showMessage('Driver Certifications Updated');
  }

  public startAdding() {
    this.editingState = '';
    this.confirmingRemoval = null;
    this.permitForm.reset({ acknowledgedAccountability: false, permitNumber: '' });
    this.permitForm.get('stateAbbreviation').enable();
  }

  public startEditing(permit: GrossWeightPermit) {
    this.editingState = permit.stateAbbreviation;
    this.confirmingRemoval = null;
    this.permitForm.reset({
      stateAbbreviation: permit.stateAbbreviation,
      expiresOn: parseISO(permit.expiresOn),
      effectiveOn: permit.effectiveOn ? parseISO(permit.effectiveOn) : null,
      permitNumber: permit.permitNumber || '',
      // Re-acknowledged on every save, not carried over from the last one.
      acknowledgedAccountability: false,
    });
    this.permitForm.get('stateAbbreviation').disable();
  }

  public cancelEditing() {
    this.editingState = null;
  }

  public async savePermit() {
    if (this.permitForm.invalid || this.saving$$.value) {
      this.permitForm.markAllAsTouched();
      return;
    }
    const value = this.permitForm.getRawValue();
    const driverId = await this.driverId$.pipe(take(1)).toPromise();

    this.saving$$.next(true);
    try {
      await this.permitsService.savePermit(driverId, value.stateAbbreviation, {
        // Local calendar dates, so the date picked is the date sent.
        expiresOn: format(value.expiresOn, 'yyyy-MM-dd'),
        effectiveOn: value.effectiveOn ? format(value.effectiveOn, 'yyyy-MM-dd') : null,
        permitNumber: (value.permitNumber || '').trim() || null,
        acknowledgedAccountability: value.acknowledgedAccountability,
      });
      this.snackBar.showMessage('Overweight permit saved');
      this.editingState = null;
      await this.loadPermits(driverId);
    } catch (error) {
      this.snackBar.showError(permitErrorMessage(error, 'Could not save the overweight permit'));
    } finally {
      this.saving$$.next(false);
      this.cd.markForCheck();
    }
  }

  public async removePermit(permit: GrossWeightPermit) {
    if (this.confirmingRemoval !== permit.stateAbbreviation) {
      this.confirmingRemoval = permit.stateAbbreviation;
      return;
    }
    const driverId = await this.driverId$.pipe(take(1)).toPromise();

    this.saving$$.next(true);
    try {
      await this.permitsService.removePermit(driverId, permit.stateAbbreviation);
      this.snackBar.showMessage(`${permit.stateName} permit removed`);
      this.confirmingRemoval = null;
      await this.loadPermits(driverId);
    } catch (error) {
      this.snackBar.showError(permitErrorMessage(error, 'Could not remove the overweight permit'));
    } finally {
      this.saving$$.next(false);
      this.cd.markForCheck();
    }
  }

  public stateLabel(limit: GrossWeightStateLimit): string {
    return `${limit.stateName} (up to ${limit.maxGrossWeightPounds.toLocaleString()} lbs)`;
  }

  public trackByState(_: number, item: { stateAbbreviation: string }): string {
    return item.stateAbbreviation;
  }

  private async loadStateLimits() {
    try {
      this.stateLimits$$.next(await this.permitsService.stateLimits());
    } catch (error) {
      this.snackBar.showError(permitErrorMessage(error, 'Could not load state weight limits'));
    }
  }

  private async loadPermits(driverId: string) {
    this.loadingPermits$$.next(true);
    try {
      this.permits$$.next(await this.permitsService.driverPermits(driverId));
    } catch (error) {
      this.snackBar.showError(permitErrorMessage(error, 'Could not load overweight permits'));
    } finally {
      this.loadingPermits$$.next(false);
      this.cd.markForCheck();
    }
  }
}
